<script setup lang="ts">
import { LazyConfirmModal } from '#components'
import type { LinkItem } from '~/stores/links'
import { DEFAULT_SECTION_TITLES, type TextSettings, type ThemeSettings, type SectionSettings } from '~/stores/auth'

const props = defineProps<{
  links: LinkItem[]
  textSettings: TextSettings
  themeSettings: ThemeSettings
  sectionSettings: SectionSettings
  editMode?: boolean
  characterId?: string
}>()

const emit = defineEmits<{
  'open-links-manager': []
}>()

const toast = useToast()
const overlay = useOverlay()
const confirmModal = overlay.create(LazyConfirmModal)

function getIcon(link: LinkItem): string {
  if (link.icon_url) return ''
  return link.link?.icon || 'i-lucide-globe'
}

function getFullUrl(link: LinkItem): string {
  const prefix = link.link?.prefix || 'https://'
  return link.value.startsWith('http') ? link.value : `${prefix}${link.value}`
}

function getTooltipText(link: LinkItem): string {
  if (link.label) return link.label
  return link.link?.name || link.value || 'Link'
}

// Track click (fire-and-forget, only in non-edit mode)
function trackClick(link: LinkItem) {
  if (props.characterId && !props.editMode) {
    $fetch(`/api/character/${props.characterId}/link/${link.id}/click`, {
      method: 'POST',
      body: { sessionId: useStatisticsSession() }
    }).catch(() => {})
  }
}

// Show confirm modal then open URL (for custom links with arbitrary user-entered URLs)
async function showConfirmAndOpen(link: LinkItem) {
  const url = getFullUrl(link)
  const confirmed = await confirmModal.open({
    title: 'Open external link',
    description: `You are about to visit:\n${url}`,
    confirmText: 'Open link',
    confirmColor: 'primary'
  }).result
  if (!confirmed) return
  trackClick(link)
  window.open(url, '_blank', 'noopener,noreferrer')
}

// Unified click handler for all link elements
function onLinkClick(link: LinkItem, e: Event) {
  if (link.is_url && link.value) {
    if (link.link_id === 'custom') {
      // Custom links have arbitrary user-entered URLs - always confirm
      e.preventDefault()
      showConfirmAndOpen(link)
    } else {
      // Known link types (GitHub, Twitter, etc.) - safe, let navigate
      trackClick(link)
    }
  } else if (link.value) {
    // Non-URL link - copy to clipboard
    navigator.clipboard.writeText(link.value)
    toast.add({
      title: 'Copied to clipboard!',
      description: link.value,
      icon: 'i-lucide-copy',
      color: 'success',
      duration: 2000
    })
    trackClick(link)
  }
}

// Dynamic tag + attrs for rendering links as <NuxtLink> or <button>
const NuxtLinkComponent = resolveComponent('NuxtLink')

function linkTag(link: LinkItem) {
  return link.is_url && link.value ? NuxtLinkComponent : 'button'
}

function linkAttrs(link: LinkItem): Record<string, string> {
  if (!link.is_url || !link.value) return {}
  // NuxtLink auto-adds rel="noopener noreferrer" for external URLs
  return { to: getFullUrl(link), target: '_blank' }
}

const iconStyle = computed(() => props.themeSettings.iconStyle || 'default')
const iconSize = computed(() => props.themeSettings.iconSize || 40)
const linkIconSize = computed(() => Math.round(iconSize.value * 0.5))

const containerAnimClass = computed(() => {
  const map: Record<string, string> = {
    bounce: 'animate-icon-bounce',
    pulse: 'animate-icon-pulse',
    wave: 'animate-icon-wave',
    shake: 'animate-icon-shake',
    flicker: 'animate-icon-flicker',
    neon: 'animate-icon-neon'
  }
  return map[iconStyle.value] || ''
})

const iconAnimClass = computed(() => {
  const map: Record<string, string> = {
    spin: 'animate-icon-spin',
    rainbow: 'animate-icon-rainbow',
    glitch: 'animate-icon-glitch'
  }
  return map[iconStyle.value] || ''
})

function getContainerStyle(link: LinkItem, index: number) {
  const primaryColor = props.themeSettings.primaryColor
  const linkColor = link.link?.icon_color || '#FFFFFF'
  const style: Record<string, string> = {
    color: `color-mix(in srgb, ${props.textSettings.color} 60%, transparent)`,
    width: `${iconSize.value}px`,
    height: `${iconSize.value}px`
  }

  if (iconStyle.value === 'bordered') {
    style.borderWidth = '1.5px'
    style.borderColor = `color-mix(in srgb, ${primaryColor} 40%, transparent)`
  }

  if (iconStyle.value === 'glow') {
    style.filter = `drop-shadow(0 0 5px color-mix(in srgb, ${linkColor} 40%, transparent)) drop-shadow(0 0 12px color-mix(in srgb, ${linkColor} 20%, transparent))`
  }

  if (iconStyle.value === 'neon') {
    style.filter = `drop-shadow(0 0 4px ${linkColor}) drop-shadow(0 0 12px ${linkColor}) drop-shadow(0 0 24px ${linkColor}) drop-shadow(0 0 48px ${linkColor})`
  }

  if (iconStyle.value === 'wave') {
    style.animationDelay = `${index * 0.15}s`
  }

  return style
}

function getIconStyle(link: LinkItem) {
  const linkColor = link.link?.icon_color || '#FFFFFF'
  return {
    color: linkColor,
    width: `${linkIconSize.value}px`,
    height: `${linkIconSize.value}px`
  }
}

function getIconWrapperStyle(link: LinkItem, index: number) {
  const linkColor = link.link?.icon_color || '#FFFFFF'
  const primaryColor = props.themeSettings.primaryColor
  const style: Record<string, string> = {}

  if (iconStyle.value === 'bordered') {
    style.borderWidth = '1.5px'
    style.borderStyle = 'solid'
    style.borderColor = `color-mix(in srgb, ${primaryColor} 40%, transparent)`
    style.borderRadius = '0.375rem'
    style.padding = '2px'
  }

  if (iconStyle.value === 'glow') {
    style.filter = `drop-shadow(0 0 5px color-mix(in srgb, ${linkColor} 40%, transparent)) drop-shadow(0 0 12px color-mix(in srgb, ${linkColor} 20%, transparent))`
  }

  if (iconStyle.value === 'neon') {
    style.filter = `drop-shadow(0 0 4px ${linkColor}) drop-shadow(0 0 12px ${linkColor}) drop-shadow(0 0 24px ${linkColor}) drop-shadow(0 0 48px ${linkColor})`
  }

  if (iconStyle.value === 'wave') {
    style.animationDelay = `${index * 0.15}s`
  }

  return style
}

const linkHoverBg = computed(() => {
  return `color-mix(in srgb, ${props.themeSettings.primaryColor} 10%, transparent)`
})

const linkStyle = computed(() => props.themeSettings.linkStyle || 'icon')

// Track embed links that failed to load (invalid invite) - fall back to regular link
const failedEmbedIds = ref(new Set<string>())

function onEmbedFailed(linkId: string) {
  failedEmbedIds.value = new Set([...failedEmbedIds.value, linkId])
}

function isEmbedLink(link: LinkItem): boolean {
  return link.show_as_embed && link.is_url && link.link_id === 'discord' && !failedEmbedIds.value.has(link.id)
}

// Group links: consecutive regular links grouped together, embed links as standalone entries
interface LinkGroup {
  type: 'regular' | 'embed'
  links: LinkItem[]
}

const linkGroups = computed((): LinkGroup[] => {
  const groups: LinkGroup[] = []
  for (const link of props.links) {
    if (isEmbedLink(link)) {
      groups.push({ type: 'embed', links: [link] })
    } else {
      const last = groups[groups.length - 1]
      if (last && last.type === 'regular') {
        last.links.push(link)
      } else {
        groups.push({ type: 'regular', links: [link] })
      }
    }
  }
  return groups
})

// Heading and divider styles
const injectedResolvedFont = inject<ComputedRef<string>>('resolvedFontFamily', computed(() => ''))
const headingStyle = computed(() => ({
  fontSize: `calc(1rem * ${props.textSettings.fontScale})`,
  fontWeight: props.textSettings.fontWeight,
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 70%, transparent)`,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.1em'
}))

const injectedSeparatorStyle = inject<Ref<Record<string, string> | null>>('separatorStyle', ref({ background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' }))
const dividerStyle = computed(() => injectedSeparatorStyle.value ?? { background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' })

// Section title visibility
const inCard = inject<Ref<boolean>>('sectionInCard', ref(false))
const showTitles = computed(() => props.sectionSettings.showTitles !== false)

// Custom section titles
const authStore = useAuthStore()
const { requirePlan } = usePlanGate()
const sectionTitlesEnabled = computed(() => authStore.canAccessBoolean('customSectionTitles'))
const sectionTitle = computed(() => props.sectionSettings.titles?.links || DEFAULT_SECTION_TITLES.links)
const updateSectionTitle = inject<(key: string, value: string) => void>('updateSectionTitle', () => {})

function getLinkLabel(link: LinkItem): string {
  return link.label || link.link?.name || 'Link'
}
</script>

<template>
  <div
    v-if="links.length > 0 || editMode"
    :class="{ 'mt-6': showTitles || !inCard }"
  >
    <!-- Section heading with divider -->
    <div
      v-if="showTitles || !inCard"
      class="flex items-center gap-3 mb-4"
    >
      <div
        class="flex-1 h-px"
        :style="dividerStyle"
      />
      <!-- Edit mode + Pro+ → click to edit -->
      <InlineEditText
        v-if="showTitles && editMode && sectionTitlesEnabled"
        :model-value="sectionSettings.titles?.links ?? ''"
        :editable="true"
        tag="h3"
        :placeholder="DEFAULT_SECTION_TITLES.links"
        :display-style="headingStyle"
        class="shrink-0"
        @update:model-value="updateSectionTitle('links', $event)"
      />
      <!-- Edit mode + Free → locked with Pro badge -->
      <div
        v-else-if="showTitles && editMode"
        class="shrink-0 inline-flex items-center gap-1.5 cursor-pointer rounded-lg transition-colors hover:bg-white/5 px-2 py-0.5"
        @click="requirePlan('Custom Section Titles', 'pro')"
      >
        <h3
          class="text-xs"
          :style="headingStyle"
        >
          {{ DEFAULT_SECTION_TITLES.links }}
        </h3>
        <UBadge
          color="fuchsia"
          variant="solid"
          size="xs"
        >
          <UIcon
            name="i-lucide-pencil"
            class="size-2.5"
          />
        </UBadge>
      </div>
      <!-- Public view → static text -->
      <h3
        v-else-if="showTitles"
        class="text-xs shrink-0"
        :style="headingStyle"
      >
        {{ sectionTitle }}
      </h3>
      <div
        v-if="showTitles"
        class="flex-1 h-px"
        :style="dividerStyle"
      />
    </div>

    <div class="space-y-3">
      <template
        v-for="(group, gi) in linkGroups"
        :key="gi"
      >
        <!-- Discord server embed -->
        <DiscordServerEmbed
          v-if="group.type === 'embed'"
          :invite-code="group.links[0].value"
          :theme-settings="themeSettings"
          :class="linkStyle === 'card' ? 'w-full' : 'max-w-sm mx-auto'"
          @load-failed="onEmbedFailed(group.links[0].id)"
        />

        <!-- Icon style (default) -->
        <div
          v-else-if="linkStyle === 'icon'"
          class="flex items-center justify-center gap-2 flex-wrap"
        >
          <UTooltip
            v-for="(link, index) in group.links"
            :key="link.id"
            :text="getTooltipText(link)"
            :delay-duration="300"
          >
            <component
              :is="linkTag(link)"
              v-bind="linkAttrs(link)"
              class="rounded-xl flex items-center justify-center transition-all duration-200 cursor-pointer hover:scale-105 no-underline"
              :class="[
                iconStyle === 'bordered' ? 'border' : '',
                containerAnimClass
              ]"
              :style="getContainerStyle(link, index)"
              @mouseenter="($event.currentTarget as HTMLElement).style.backgroundColor = linkHoverBg"
              @mouseleave="($event.currentTarget as HTMLElement).style.backgroundColor = 'transparent'"
              @click="onLinkClick(link, $event)"
            >
              <img
                v-if="link.icon_url"
                :src="link.icon_url"
                class="rounded object-contain"
                :style="{ width: `${linkIconSize}px`, height: `${linkIconSize}px` }"
                :alt="link.label || link.link?.name"
              >
              <UIcon
                v-else
                :name="getIcon(link)"
                :class="['transition-all duration-200', iconAnimClass]"
                :style="getIconStyle(link)"
              />
              <!-- Accessible name for icon-only links (screen readers and crawlers; invisible) -->
              <span
                v-if="!link.icon_url"
                class="sr-only"
              >{{ getLinkLabel(link) }}</span>
            </component>
          </UTooltip>
        </div>

        <!-- Card style -->
        <div
          v-else-if="linkStyle === 'card'"
          class="flex flex-col gap-2"
        >
          <component
            :is="linkTag(link)"
            v-for="(link, index) in group.links"
            :key="link.id"
            v-bind="linkAttrs(link)"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-white/6 bg-white/3 transition-all duration-200 cursor-pointer hover:bg-white/6 no-underline"
            @click="onLinkClick(link, $event)"
          >
            <span
              class="inline-flex items-center justify-center shrink-0"
              :class="containerAnimClass"
              :style="getIconWrapperStyle(link, index)"
            >
              <img
                v-if="link.icon_url"
                :src="link.icon_url"
                class="size-5 rounded object-contain"
                :alt="getLinkLabel(link)"
              >
              <UIcon
                v-else
                :name="getIcon(link)"
                class="size-5"
                :class="iconAnimClass"
                :style="{ color: link.link?.icon_color || '#FFFFFF' }"
              />
            </span>
            <span
              class="flex-1 text-left text-sm font-medium"
              :style="{ color: `color-mix(in srgb, ${textSettings.color} 80%, transparent)` }"
            >
              {{ getLinkLabel(link) }}
            </span>
            <UIcon
              name="i-lucide-external-link"
              class="size-3.5 text-white/20"
            />
          </component>
        </div>

        <!-- Pill style -->
        <div
          v-else-if="linkStyle === 'pill'"
          class="flex items-center justify-center gap-2 flex-wrap"
        >
          <component
            :is="linkTag(link)"
            v-for="(link, index) in group.links"
            :key="link.id"
            v-bind="linkAttrs(link)"
            class="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/6 transition-all duration-200 cursor-pointer hover:bg-white/10 hover:scale-105 no-underline"
            @click="onLinkClick(link, $event)"
          >
            <span
              class="inline-flex items-center justify-center shrink-0"
              :class="containerAnimClass"
              :style="getIconWrapperStyle(link, index)"
            >
              <img
                v-if="link.icon_url"
                :src="link.icon_url"
                class="size-4 rounded object-contain"
                :alt="getLinkLabel(link)"
              >
              <UIcon
                v-else
                :name="getIcon(link)"
                class="size-4"
                :class="iconAnimClass"
                :style="{ color: link.link?.icon_color || '#FFFFFF' }"
              />
            </span>
            <span
              class="text-xs font-medium"
              :style="{ color: `color-mix(in srgb, ${textSettings.color} 70%, transparent)` }"
            >
              {{ getLinkLabel(link) }}
            </span>
          </component>
        </div>

        <!-- Minimal style -->
        <div
          v-else-if="linkStyle === 'minimal'"
          class="flex flex-col items-center gap-1"
        >
          <component
            :is="linkTag(link)"
            v-for="link in group.links"
            :key="link.id"
            v-bind="linkAttrs(link)"
            class="text-sm transition-all duration-200 cursor-pointer hover:opacity-100 border-b border-transparent hover:border-white/20 pb-0.5 no-underline"
            :style="{ color: `color-mix(in srgb, ${textSettings.color} 50%, transparent)` }"
            @click="onLinkClick(link, $event)"
          >
            {{ getLinkLabel(link) }}
          </component>
        </div>

        <!-- Outline style -->
        <div
          v-else-if="linkStyle === 'outline'"
          class="flex items-center justify-center gap-2 flex-wrap"
        >
          <component
            :is="linkTag(link)"
            v-for="(link, index) in group.links"
            :key="link.id"
            v-bind="linkAttrs(link)"
            class="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 cursor-pointer hover:scale-105 no-underline"
            :style="{
              borderColor: `color-mix(in srgb, ${link.link?.icon_color || themeSettings.primaryColor} 40%, transparent)`,
              color: `color-mix(in srgb, ${textSettings.color} 70%, transparent)`
            }"
            @mouseenter="($event.currentTarget as HTMLElement).style.backgroundColor = `color-mix(in srgb, ${link.link?.icon_color || themeSettings.primaryColor} 10%, transparent)`"
            @mouseleave="($event.currentTarget as HTMLElement).style.backgroundColor = 'transparent'"
            @click="onLinkClick(link, $event)"
          >
            <span
              class="inline-flex items-center justify-center shrink-0"
              :class="containerAnimClass"
              :style="getIconWrapperStyle(link, index)"
            >
              <img
                v-if="link.icon_url"
                :src="link.icon_url"
                class="size-4 rounded object-contain"
                :alt="getLinkLabel(link)"
              >
              <UIcon
                v-else
                :name="getIcon(link)"
                class="size-4"
                :class="iconAnimClass"
                :style="{ color: link.link?.icon_color || '#FFFFFF' }"
              />
            </span>
            <span class="text-xs font-medium">
              {{ getLinkLabel(link) }}
            </span>
          </component>
        </div>
      </template>

      <!-- Edit mode manage button -->
      <div
        v-if="editMode"
        class="flex justify-center"
      >
        <UTooltip
          v-if="linkStyle === 'icon'"
          text="Manage links"
          :delay-duration="300"
        >
          <UButton
            variant="ghost"
            color="neutral"
            icon="i-lucide-settings"
            class="size-10 flex items-center justify-center rounded-xl border-2 border-dashed border-white/12 text-white/30 hover:text-white/50 hover:scale-105 transition-all duration-200"
            @click="emit('open-links-manager')"
          />
        </UTooltip>
        <UButton
          v-else-if="linkStyle === 'card'"
          variant="ghost"
          color="neutral"
          icon="i-lucide-settings"
          label="Manage links"
          block
          class="rounded-xl border-2 border-dashed border-white/10 text-white/20 hover:text-white/40 hover:bg-white/4 transition-all duration-200"
          @click="emit('open-links-manager')"
        />
        <UButton
          v-else-if="linkStyle === 'pill'"
          variant="ghost"
          color="neutral"
          icon="i-lucide-settings"
          class="rounded-full border-2 border-dashed border-white/10 text-white/20 hover:text-white/40 hover:bg-white/4 transition-all duration-200"
          @click="emit('open-links-manager')"
        />
        <UButton
          v-else-if="linkStyle === 'minimal'"
          variant="ghost"
          color="neutral"
          icon="i-lucide-settings"
          label="Manage links"
          class="text-sm text-white/20 border-b border-dashed border-white/10 pb-0.5 hover:text-white/40"
          @click="emit('open-links-manager')"
        />
        <UButton
          v-else-if="linkStyle === 'outline'"
          variant="ghost"
          color="neutral"
          icon="i-lucide-settings"
          class="rounded-lg border-2 border-dashed border-white/10 text-white/20 hover:text-white/40 hover:bg-white/4 transition-all duration-200"
          @click="emit('open-links-manager')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Bounce */
.animate-icon-bounce {
  animation: icon-bounce 1.8s ease-in-out infinite;
}
@keyframes icon-bounce {
  0%, 100% { transform: translateY(0); }
  40% { transform: translateY(-5px); }
  60% { transform: translateY(-2px); }
}

/* Pulse - breathing scale */
.animate-icon-pulse {
  animation: icon-pulse 2s ease-in-out infinite;
}
@keyframes icon-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

/* Wave - staggered float (delay set via inline style) */
.animate-icon-wave {
  animation: icon-wave 2.5s ease-in-out infinite;
}
@keyframes icon-wave {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* Neon - subtle brightness pulse paired with strong drop-shadow */
.animate-icon-neon {
  animation: icon-neon 2s ease-in-out infinite;
}
@keyframes icon-neon {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

/* Shake - rapid vibration */
.animate-icon-shake {
  animation: icon-shake 0.4s ease-in-out infinite;
}
@keyframes icon-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
}

/* Flicker - neon sign stutter */
.animate-icon-flicker {
  animation: icon-flicker 3s steps(1) infinite;
}
@keyframes icon-flicker {
  0%, 100% { opacity: 1; }
  5% { opacity: 0.3; }
  10% { opacity: 1; }
  15% { opacity: 0.6; }
  20% { opacity: 1; }
  55% { opacity: 1; }
  57% { opacity: 0.2; }
  60% { opacity: 1; }
  80% { opacity: 1; }
  82% { opacity: 0.4; }
  85% { opacity: 1; }
}

/* Spin - slow rotation (icon only) */
.animate-icon-spin {
  animation: icon-spin 3s linear infinite;
}
@keyframes icon-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Rainbow - hue cycling (icon only) */
.animate-icon-rainbow {
  animation: icon-rainbow 3s linear infinite;
}
@keyframes icon-rainbow {
  from { filter: hue-rotate(0deg); }
  to { filter: hue-rotate(360deg); }
}

/* Glitch - RGB shift + jitter (icon only) */
.animate-icon-glitch {
  animation: icon-glitch 2s steps(1) infinite;
}
@keyframes icon-glitch {
  0%, 30%, 60%, 100% { transform: translate(0); filter: none; }
  10% { transform: translate(-2px, 1px); filter: hue-rotate(90deg); }
  20% { transform: translate(2px, -1px); filter: hue-rotate(200deg); }
  40% { transform: translate(1px, 2px); filter: hue-rotate(270deg); }
  50% { transform: translate(-1px, -2px); filter: hue-rotate(45deg); }
}
</style>
